import React, { useEffect, useState } from 'react';
import { useSafeThemeClasses } from '@/shared/theme';
import { adminApiService } from '@/shared/services/adminApiService';
import { logger, dateUtils } from '@/shared/utils';

type ActivityLog = {
  id: string;
  user_id: string | null;
  event_type: string;
  entity_type: string;
  entity_id: string | null;
  action: string;
  description: string;
  metadata: Record<string, unknown>;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string | null;
};

type EntityFilter = 'all' | 'user' | 'blog' | 'system';

const entityLabel = (entityType: string): string => {
  const labels: Record<string, string> = {
    user: 'User',
    blog: 'Blog',
    meeting: 'Meeting',
    system: 'System',
    error: 'Error',
  };
  return labels[entityType] || entityType;
};

const AdminActivityPage: React.FC = () => {
  const theme = useSafeThemeClasses();
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [total, setTotal] = useState(0);
  const [entityFilter, setEntityFilter] = useState<EntityFilter>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    const loadLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await adminApiService.getSystemLogs({
          limit: 100,
          // Errors already have their own dedicated screen - exclude them
          // here by default so the two screens don't show duplicate content.
          entity_type: entityFilter === 'all' ? undefined : entityFilter,
        });
        if (response.success && response.data) {
          const rows = entityFilter === 'all'
            ? response.data.logs.filter((log) => log.entity_type !== 'error')
            : response.data.logs;
          setLogs(rows);
          setTotal(response.data.pagination.total);
        } else {
          setError(response.error?.message || 'Failed to load activity log');
          logger.error('Failed to load activity log', 'AdminActivityPage', { error: response.error });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load activity log');
        logger.error('Network error loading activity log', 'AdminActivityPage', { error: err });
      } finally {
        setLoading(false);
      }
    };

    loadLogs();
  }, [entityFilter]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className={`${theme.card} rounded-2xl p-12 text-center`}>
          <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${theme.textAccent} mx-auto mb-4`}></div>
          <p className={theme.textSecondary}>Loading activity...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h2 className={`text-2xl font-bold mb-1 ${theme.textHeading} tracking-tight`}>Activity</h2>
        <p className={theme.textSecondary}>What's happened across the project, newest first.</p>
      </div>

      {error && (
        <div className={`${theme.alertError} rounded-xl p-4`}>
          <p className={theme.textError}>{error}</p>
        </div>
      )}

      <div className="flex gap-2">
        {([
          { id: 'all', label: 'All' },
          { id: 'user', label: 'Users' },
          { id: 'blog', label: 'Blogs' },
          { id: 'system', label: 'System' },
        ] as const).map((filterOption) => (
          <button
            key={filterOption.id}
            onClick={() => setEntityFilter(filterOption.id)}
            className={`px-4 py-2 rounded-xl transition capitalize ${
              entityFilter === filterOption.id
                ? `${theme.buttonPrimary} ${theme.buttonTextPrimary}`
                : `${theme.buttonSecondary} ${theme.textSecondary}`
            }`}
          >
            <span className={entityFilter === filterOption.id ? theme.textPrimary : theme.textSecondary}>
              {filterOption.label}
            </span>
          </button>
        ))}
      </div>

      <div className={`${theme.card} rounded-3xl p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-xl font-semibold ${theme.textHeading}`}>Recent Activity</h3>
          <span className={`text-sm ${theme.textMuted}`}>{total.toLocaleString()} total</span>
        </div>

        {logs.length === 0 ? (
          <p className={`text-center py-8 ${theme.textMuted}`}>Nothing here yet.</p>
        ) : (
          <div className="space-y-3">
            {logs.map((log) => {
              const isExpanded = expandedId === log.id;
              const hasMetadata = log.metadata && Object.keys(log.metadata).length > 0;
              return (
                <div
                  key={log.id}
                  className={`p-4 rounded-lg border border-opacity-10 transition-all duration-300 ${theme.textPrimary}`}
                >
                  <button
                    className="w-full text-left"
                    onClick={() => setExpandedId(isExpanded ? null : log.id)}
                    disabled={!hasMetadata && !log.ip_address}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${theme.badge} ${theme.textAccent}`}>
                            {entityLabel(log.entity_type)}
                          </span>
                          <span className={`text-xs ${theme.textMuted}`}>
                            {log.created_at ? dateUtils.format(log.created_at, 'LONG') : 'Unknown time'}
                          </span>
                        </div>
                        <p className={`font-medium truncate ${theme.textHeading}`}>{log.description}</p>
                      </div>
                      {(hasMetadata || log.ip_address) && (
                        <span className={`text-xs shrink-0 ${theme.textMuted}`}>{isExpanded ? 'Hide' : 'Details'}</span>
                      )}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className={`mt-3 pt-3 border-t border-opacity-10 space-y-2 text-sm ${theme.textSecondary}`}>
                      {hasMetadata && (
                        <pre className={`whitespace-pre-wrap break-words text-xs ${theme.textMuted}`}>
                          {JSON.stringify(log.metadata, null, 2)}
                        </pre>
                      )}
                      <div className={`flex flex-wrap gap-x-4 gap-y-1 text-xs ${theme.textMuted}`}>
                        {log.ip_address && <span>IP: {log.ip_address}</span>}
                        {log.user_id && <span>User: {log.user_id}</span>}
                        <span>Action: {log.action}</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminActivityPage;
